/**
 * The NPC vendor window: chalk trade arrows and the restock plate.
 *
 * One slice of the base-view stylesheet; see styles/index.ts. Loaded after
 * commerce and item, whose .bv-vendor-rot and .bv-trade-mark rules it
 * restyles, so source order plus a parent selector wins cleanly.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it.
 */
export default `
/* -- Chalk trade arrows ----------------------------------------------------
   The old brass SVG stays in the markup (a test pins it) but is hidden; the
   mark is now a chalk drawing on the mark box itself. Purely decorative. */
.bv-trade-mark {
  width: 96px; height: 78px; pointer-events: none;
  background: url('/ui/trade-arrows.webp') center / contain no-repeat;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.7)) drop-shadow(0 0 6px rgba(255,244,220,0.14));
  opacity: 0.92; transform: rotate(-3deg);
}
.bv-trade-mark svg { display: none; }
@media (max-width: 760px) {
  /* Stacked blocks: the exchange runs top to bottom, so the arrows turn
     upright and point up and down. */
  .bv-trade-mark { width: 64px; height: 64px; transform: rotate(90deg); }
}

/* -- Restock plate ---------------------------------------------------------
   A worn tag screwed to the window: plate face, grime, a hazard strip on top,
   chalk numerals and a bar that drains over the time left. The bar, the warn
   colour and the pulse are CSS animations whose durations and delays come
   from custom properties set inline by rotationChip, so the once-a-second
   ticker only has to rewrite the text. */
.bv-vendor-head .bv-vendor-rot {
  position: relative; isolation: isolate; overflow: hidden;
  /* Defaults only; rotationChip overrides all four inline. Declared here so the
     token audit sees where they come from. */
  --rot-from: 100%; --rot-left: 0ms; --rot-warn-at: 0ms; --rot-pulse-at: 0ms;
  min-width: 132px; padding: 9px 12px 8px;
  align-items: stretch; gap: 2px;
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    linear-gradient(rgba(14,9,5,0.78), rgba(14,9,5,0.88)),
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: 0 0 0 1px #120d08, 0 4px 12px rgba(0,0,0,0.55), inset 0 0 14px rgba(0,0,0,0.55);
}
.bv-vendor-head .bv-vendor-rot::before {
  content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
  background:
    repeating-linear-gradient(135deg, #d9a020 0 8px, #17110a 8px 16px) 0 0 / 100% 3px no-repeat,
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 40% / 300px 300px;
  opacity: 0.75;
}
.bv-vendor-head .bv-vendor-rot-label {
  align-self: flex-start; margin-top: 2px;
  font: 700 0.6rem var(--font-technical, var(--font-display));
  letter-spacing: 0.22em; color: var(--bone-dim);
}
.bv-vendor-head .bv-vendor-rot-value {
  align-self: center; margin: 1px 0 3px;
  font: 800 1.5rem/1.1 var(--font-display); letter-spacing: 0.06em;
  color: #efe6d2; font-variant-numeric: tabular-nums;
  /* Chalk: soft cream face with a dusty halo and a hard shadow, no brass glow. */
  text-shadow: 0 0 1px rgba(255,248,230,0.9), 0 0 9px rgba(239,230,210,0.28), 1px 2px 0 rgba(0,0,0,0.7);
  animation: bv-rot-warn 1ms linear var(--rot-warn-at, 0ms) both;
}
.bv-vendor-rot-bar {
  display: block; height: 7px; border-radius: 2px; overflow: hidden;
  background: rgba(0,0,0,0.6); box-shadow: inset 0 1px 2px rgba(0,0,0,0.8), 0 0 0 1px rgba(122,90,48,0.55);
}
.bv-vendor-rot-fill {
  display: block; height: 100%; width: var(--rot-from, 100%); transform-origin: left center;
  background: linear-gradient(90deg, #7a5a30, var(--brass-hot, #e0a850));
  animation:
    bv-rot-drain var(--rot-left, 0ms) linear both,
    bv-rot-fill-warn 1ms linear var(--rot-warn-at, 0ms) both;
}
@keyframes bv-rot-drain { to { width: 0%; } }
@keyframes bv-rot-warn { to { color: var(--rust, #d2553a); text-shadow: 0 0 1px rgba(255,200,180,0.9), 0 0 10px rgba(210,85,58,0.5), 1px 2px 0 rgba(0,0,0,0.7); } }
@keyframes bv-rot-fill-warn { to { background: linear-gradient(90deg, #8f2f1e, var(--rust, #d2553a)); } }
@keyframes bv-rot-pulse { 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: no-preference) {
  .bv-vendor-head .bv-vendor-rot {
    animation: bv-rot-pulse 1s ease-in-out var(--rot-pulse-at, 0ms) infinite both;
  }
}
@media (max-width: 480px) {
  .bv-vendor-head .bv-vendor-rot { flex: 1 1 100%; }
}
`;
